// hud3d.ts — cum SCRIE scena 3D: etichete, marcaje și cote.
//
// Două stiluri, după ce spune scrisul:
//
//   * ADNOTAREA (numele unui strat de pământ, apa) e a animațiilor tehnice:
//     un PUNCT alb pe obiect, un LIDER subțire care pleacă din el și cotește
//     în unghi drept, și la capăt TEXTUL, subțire, scris direct pe gri-ul
//     scenei, cu litere de tipar: `[de la–până la m] NUMELE STRATULUI`. Stă cu fața la cameră și are aceeași mărime pe
//     ecran la orice zoom: e o etichetă, nu face parte din desen;
//   * COTA (o lungime, o înălțime, o cotă de nivel) e a planșei: linie de
//     cotă cu SĂGEȚI, ținută la distanță de clădire, MARTORI subțiri și
//     continui care pleacă de pe muchia cotată și trec puțin peste linia de
//     cotă — ei sunt «liniile din capete» după care ochiul găsește ce anume
//     s-a măsurat — și valoarea scrisă ÎN LUNGUL cotei, în planul ei, cu aer
//     între ea și linie. Se rotește cu modelul, ca pe un desen tehnic
//     desfășurat în 3D. Textul e în metri, nu în pixeli: la un bloc de 40 m
//     cotele sunt mai mari decât la o casă de 8, cum ar fi pe planșă.
//
// Piesele adnotării:
//
//   * MARCAJUL (discul alb cu inel de cerneală) spune «aici». E singurul lucru
//     care stă PE obiect; tot restul stă în afara lui, ca desenul să rămână
//     curat;
//   * LIDERUL e hairline, o singură grosime, și cotește ortogonal (orizontal
//     apoi vertical, sau invers) — niciodată oblic. Când eticheta stă chiar în
//     dreptul marcajului, cotul are lungime zero și liderul e o linie dreaptă;
//   * TEXTUL e un singur rând, o singură culoare (cerneala), un singur corp.
//     NICIUN fundal propriu și NICIUN contur: singurul fundal e gri-ul
//     scenei. Adâncimea stă în paranteze drepte înaintea numelui, ca să se
//     citească întâi unde și apoi ce.
//
// Textul se desenează pe o pânză 2D și se pune pe un `Sprite`: sprite-ul stă
// mereu cu fața la cameră, deci eticheta rămâne dreaptă din orice unghi de
// orbitare. MĂRIMEA lui nu e în metri, ci în PIXELI: `scaleazaHud` o
// recalculează la fiecare cadru din distanța până la cameră, deci o etichetă
// se citește la fel de la 3 m și de la 300.

import * as THREE from "three";
import { PAL } from "./paletaDesen";
import { mFmt } from "./cote";

export { mFmt };

/** Înălțimea textului pe pânză. Nu e mărimea din scenă — e rezoluția la care
 *  se rasterizează, deci cât de curat arată la zoom. */
const TEXT_PX = 44;
/** Cât de înalt e rândul unei etichete PE ECRAN, în pixeli. */
const ETICHETA_ECRAN_PX = 12;
/** Diametrul marcajului pe ecran, în pixeli. */
const MARCAJ_ECRAN_PX = 9;
/** Strângerea pânzei pe orizontală (1 = litera la lățimea ei firească). */
const CONDENSARE = 1;

const FONT = `400 ${TEXT_PX}px "Helvetica Neue", Helvetica, Arial, `
  + "system-ui, sans-serif";

function spriteHud(cv: HTMLCanvasElement, ecran_px: number,
                   inaltime_m: number) {
  const tex = new THREE.CanvasTexture(cv);
  tex.anisotropy = 4;
  const sp = new THREE.Sprite(new THREE.SpriteMaterial({
    map: tex, transparent: true, depthTest: false, depthWrite: false,
  }));
  const aspect = cv.width / cv.height;
  sp.scale.set(inaltime_m * aspect, inaltime_m, 1);
  sp.renderOrder = 10;
  // mărimea CERUTĂ pe ecran, în pixeli: `scaleazaHud` o impune la fiecare
  // cadru, ca zoom-ul să nu schimbe cât de mare e scrisul
  sp.userData.hud = { px: ecran_px, aspect };
  return sp;
}

/** O etichetă: un rând de text în cerneală, scris DIRECT pe scenă — fără
 *  plăcuță, fără chenar, fără halou. Ancorată la STÂNGA (liderul vine din
 *  stânga) sau la dreapta cu `spreDreapta = false`. `inaltime_m` e mărimea
 *  din scenă înainte de primul cadru; pe ecran o impune `scaleazaHud`.
 *  Întoarce `null` când pânza 2D nu există (jsdom, în teste) — scena se
 *  construiește la fel, doar fără scris. */
export function eticheta(
  text: string,
  inaltime_m: number,
  spreDreapta = true,
): THREE.Sprite | null {
  const cv = document.createElement("canvas");
  const c = cv.getContext("2d");
  if (!c) return null;

  const spatiere = TEXT_PX * 0.05;          // majusculele cer puțin aer
  c.font = FONT;
  const lat = [...text].reduce(
    (s, ch) => s + c.measureText(ch).width + spatiere, 0) * CONDENSARE;
  const pad = TEXT_PX * 0.15;
  const hT = Math.ceil(TEXT_PX * 1.3);
  cv.width = Math.ceil(pad + lat + pad);
  cv.height = hT;
  const ctx = cv.getContext("2d")!;

  ctx.font = FONT;
  ctx.textBaseline = "middle";
  ctx.fillStyle = PAL.ink;
  // condensat prin strângerea pânzei pe X; literă cu literă, ca să aibă
  // spațiere
  ctx.scale(CONDENSARE, 1);
  let cx = pad / CONDENSARE;
  const y = hT / 2 + TEXT_PX * 0.04;
  for (const ch of text) {
    ctx.fillText(ch, cx, y);
    cx += ctx.measureText(ch).width + spatiere;
  }

  const sp = spriteHud(cv, ETICHETA_ECRAN_PX, inaltime_m);
  sp.center.set(spreDreapta ? 0 : 1, 0.5);
  return sp;
}

/** Marcajul de punct: disc alb cu inel de cerneală, centrat pe locul arătat.
 *  Are aceeași mărime pe ecran indiferent de zoom (`scaleazaHud`). */
export function marcaj(inaltime_m: number): THREE.Sprite | null {
  const N = 48;
  const cv = document.createElement("canvas");
  cv.width = cv.height = N;
  const c = cv.getContext("2d");
  if (!c) return null;
  c.beginPath();
  c.arc(N / 2, N / 2, N * 0.34, 0, Math.PI * 2);
  c.fillStyle = PAL.hartie;
  c.fill();
  c.lineWidth = N * 0.09;
  c.strokeStyle = PAL.ink;
  c.stroke();
  const sp = spriteHud(cv, MARCAJ_ECRAN_PX, inaltime_m);
  sp.center.set(0.5, 0.5);
  sp.renderOrder = 11;
  return sp;
}

/** Impune tuturor sprite-urilor HUD dintr-un grup mărimea lor PE ECRAN.
 *
 *  Un sprite se micșorează cu distanța, ca orice obiect din scenă — corect
 *  pentru materie, greșit pentru scris: la un teren de 25 m adâncime, ca să
 *  vezi toată prisma trebuie să te depărtezi, iar etichetele devin ilizibile.
 *  Se cheamă din bucla de randare, înainte de `renderer.render`. */
export function scaleazaHud(radacina: THREE.Object3D, camera: THREE.Camera,
                            inaltimeCanvas_px: number) {
  const persp = camera as THREE.PerspectiveCamera;
  if (!persp.isPerspectiveCamera || !inaltimeCanvas_px) return;
  const k = 2 * Math.tan(THREE.MathUtils.degToRad(persp.fov / 2))
    / inaltimeCanvas_px;
  const p = new THREE.Vector3();
  const c = new THREE.Vector3();
  camera.getWorldPosition(c);
  radacina.traverse((o) => {
    const hud = o.userData?.hud;
    if (!hud) return;
    o.getWorldPosition(p);
    const h = Math.max(k * hud.px * p.distanceTo(c), 1e-4);
    (o as THREE.Sprite).scale.set(h * hud.aspect, h, 1);
  });
}

/** Cotele la care stau etichetele: pornesc de unde le e locul, dar se împing
 *  una pe alta ca să nu se suprapună (un strat de 40 cm dintre două de 4 m
 *  nu-și poate ține eticheta pe mijloc). Intrarea e sortată DESCRESCĂTOR. */
export function fara_suprapunere(cote: number[], pas: number) {
  const out = cote.slice();
  for (let i = out.length - 2; i >= 0; i--)
    if (out[i] - out[i + 1] < pas) out[i] = out[i + 1] + pas;
  return out;
}

/** Textul tăiat la o lungime care încape pe o etichetă. */
export function scurt(text: string | null | undefined, max: number) {
  const t = String(text ?? "").replace(/\s+/g, " ").trim();
  return t.length <= max ? t : t.slice(0, max - 1).trimEnd() + "…";
}

// ---------------------------------------------------------------------------
// Liderul și cota liniară
// ---------------------------------------------------------------------------

type V3 = [number, number, number];

function adauga(out: number[], a: V3, b: V3) {
  out.push(...a, ...b);
}

/** Liderul unei etichete: linia frântă de la marcaj la plăcuță, prin
 *  colțurile date. Segmentele de lungime zero (cot inexistent) se sar, ca să
 *  nu rămână puncte moarte în geometrie. */
export function lider(out: number[], ...puncte: V3[]) {
  for (let i = 1; i < puncte.length; i++) {
    const a = puncte[i - 1];
    const b = puncte[i];
    if (Math.hypot(b[0] - a[0], b[1] - a[1], b[2] - a[2]) < 1e-6) continue;
    adauga(out, a, b);
  }
}

/** O cotă între punctele `a` și `b`, desenată pe o paralelă deplasată cu
 *  `deplasare` (vector). Capetele au SĂGEȚI pline, în planul dat de direcția
 *  cotei și de deplasare; martorii pornesc de la muchia cotată și trec puțin
 *  peste linia de cotă.
 *
 *  -> {linii, martori, sageti, mijloc, dir, nrm}: segmentele liniei, ale
 *  martorilor, triunghiurile săgeților și reperul în care se scrie valoarea
 *  (`textCota`). */
export function cotaLiniara(a: V3, b: V3, deplasare: V3, sageata_m: number,
                            normala?: THREE.Vector3) {
  const A: V3 = [a[0] + deplasare[0], a[1] + deplasare[1], a[2] + deplasare[2]];
  const B: V3 = [b[0] + deplasare[0], b[1] + deplasare[1], b[2] + deplasare[2]];
  const dir = new THREE.Vector3(B[0] - A[0], B[1] - A[1], B[2] - A[2]);
  const lung = dir.length() || 1;
  dir.multiplyScalar(1 / lung);
  const dep = new THREE.Vector3(...deplasare);
  // NORMALA cotei — planul în care se lățesc săgețile și stă scrisul. Implicit
  // e deplasarea (cota e împinsă în afara piesei, iar textul iese tot într-
  // acolo); o cotă nedeplasată o ia pe verticală.
  //
  // «Pe verticală» nu merge însă la o cotă CHIAR verticală: normala ar fi
  // chiar direcția ei, baza (dir, nrm) ar ieși degenerată, iar din ea se
  // calculează și lățimea săgeților, și orientarea scrisului — săgeți de
  // lățime zero și text întors pe dos. De aceea `normala` se poate da din
  // afară, iar dacă tot iese paralelă cu direcția se alege o perpendiculară.
  let nrm = normala ? normala.clone().normalize()
    : (dep.lengthSq() > 1e-9 ? dep.clone().normalize()
      : new THREE.Vector3(0, 0, 1));
  if (Math.abs(nrm.dot(dir)) > 0.999) {
    const alt = Math.abs(dir.z) > 0.9 ? new THREE.Vector3(1, 0, 0)
      : new THREE.Vector3(0, 0, 1);
    nrm = new THREE.Vector3().crossVectors(dir, alt).normalize();
  }

  const linii: number[] = [];
  const martori: number[] = [];
  const sageti: number[] = [];
  adauga(linii, A, B);
  // săgeata: vârful pe capăt, baza înapoi pe direcția cotei, lată cât 0,35
  // din lungime — proporția săgeții de planșă
  const lat = sageata_m * 0.35;
  ([[A, 1], [B, -1]] as [V3, number][]).forEach(([P, semn]) => {
    const baza = new THREE.Vector3(...P).addScaledVector(dir, semn * sageata_m);
    const s1 = baza.clone().addScaledVector(nrm, lat);
    const s2 = baza.clone().addScaledVector(nrm, -lat);
    sageti.push(...P, s1.x, s1.y, s1.z, s2.x, s2.y, s2.z);
  });
  // Martorul pleacă de pe muchia cotată și trece peste linia de cotă cu o
  // lungime FIXĂ, cât o săgeată — nu cu o fracțiune din depărtare. Fracțiunea
  // (15 %) dădea pe o cotă apropiată un capăt de câțiva centimetri, adică
  // invizibil: cota părea o linie plutind lângă clădire, fără să arate ce
  // măsoară.
  const peste = sageata_m * 0.8;
  ([[a, A], [b, B]] as [V3, V3][]).forEach(([P, Q]) => {
    const l = Math.hypot(Q[0] - P[0], Q[1] - P[1], Q[2] - P[2]) || 1;
    const k = (l + peste) / l;
    adauga(martori, P, [
      P[0] + (Q[0] - P[0]) * k,
      P[1] + (Q[1] - P[1]) * k,
      P[2] + (Q[2] - P[2]) * k,
    ]);
  });
  const mijloc: V3 = [(A[0] + B[0]) / 2, (A[1] + B[1]) / 2, (A[2] + B[2]) / 2];
  return { linii, martori, sageti, mijloc, A, B, dir, nrm, lung };
}

/** Valoarea unei cote, scrisă ÎN PLANUL cotei: un dreptunghi cu textul
 *  rasterizat, așezat cu baza pe direcția `dir` și cu „susul" pe `nrm`,
 *  puțin deasupra liniei de cotă. Mărimea e în METRI (`inaltime_m` e
 *  înălțimea literei), ca pe planșă. `orienteazaCote` îl întoarce la fiecare
 *  cadru ca să se citească de la stânga la dreapta (sau de jos în sus) din
 *  orice parte ar privi camera. Întoarce `null` fără pânză 2D. */
export function textCota(
  valoare: string,
  inaltime_m: number,
  mijloc: V3,
  dir: THREE.Vector3,
  nrm: THREE.Vector3,
  culoare: string = PAL.ink,
  ingrosat = false,
  /** Pastila albă de sub text: cota se citește și peste o grindă sau peste
   *  pământ, nu doar pe fundalul gol. */
  pastila = false,
): THREE.Mesh | null {
  const cv = document.createElement("canvas");
  const c = cv.getContext("2d");
  if (!c) return null;
  const font = `${ingrosat ? 600 : 400} ${TEXT_PX}px "Helvetica Neue", `
    + "Helvetica, Arial, system-ui, sans-serif";
  c.font = font;
  const lat = c.measureText(valoare).width;
  const pad = TEXT_PX * 0.25;
  cv.width = Math.ceil(lat + 2 * pad);
  cv.height = Math.ceil(TEXT_PX * 1.3);
  const ctx = cv.getContext("2d")!;
  ctx.font = font;
  ctx.textBaseline = "middle";
  ctx.textAlign = "center";
  if (pastila) {
    const r = cv.height * 0.22;
    ctx.fillStyle = "rgba(255, 255, 255, 0.92)";
    ctx.beginPath();
    ctx.roundRect(0.5, 0.5, cv.width - 1, cv.height - 1, r);
    ctx.fill();
    ctx.strokeStyle = PAL.linie;
    ctx.lineWidth = 1;
    ctx.stroke();
  }
  ctx.fillStyle = culoare;
  ctx.fillText(valoare, cv.width / 2, cv.height / 2 + 1);

  const tex = new THREE.CanvasTexture(cv);
  tex.anisotropy = 8;
  const h = inaltime_m * 1.3;
  const w = h * (cv.width / cv.height);
  const mesh = new THREE.Mesh(
    new THREE.PlaneGeometry(w, h),
    new THREE.MeshBasicMaterial({
      map: tex, transparent: true, depthTest: false, depthWrite: false,
      side: THREE.DoubleSide,
    }));
  const z = dir.clone().cross(nrm).normalize();
  const m = new THREE.Matrix4().makeBasis(dir.clone().normalize(),
                                          nrm.clone().normalize(), z);
  mesh.quaternion.setFromRotationMatrix(m);
  // deasupra liniei de cotă, cu aproape o literă de aer: lipit de linie,
  // textul se citea ca parte din ea, iar săgețile îi intrau în cifre
  mesh.position.set(...mijloc).addScaledVector(nrm, h * 0.5 + inaltime_m * 0.9);
  mesh.renderOrder = 10;
  mesh.userData.cota = true;
  return mesh;
}

/** Întoarce textele cotelor ca să se citească: o cotă de planșă se citește de
 *  la stânga la dreapta sau de jos în sus, niciodată în oglindă sau cu capul
 *  în jos. Cum camera orbitează, același dreptunghi ajunge privit din spate
 *  sau răsturnat; aici se oglindește / se rotește cu 180° pe loc. Se cheamă
 *  din bucla de randare, înainte de `renderer.render`. */
export function orienteazaCote(radacina: THREE.Object3D, camera: THREE.Camera) {
  camera.updateMatrixWorld();
  const centru = new THREE.Vector3();
  const spreCamera = new THREE.Vector3();
  const normala = new THREE.Vector3();
  const citire = new THREE.Vector3();
  const p0 = new THREE.Vector3();
  const p1 = new THREE.Vector3();
  const camPoz = new THREE.Vector3();
  camera.getWorldPosition(camPoz);
  radacina.traverse((o) => {
    if (!o.userData?.cota) return;
    o.updateMatrixWorld();
    o.getWorldPosition(centru);
    const q = o.getWorldQuaternion(new THREE.Quaternion());
    normala.set(0, 0, 1).applyQuaternion(q);
    citire.set(1, 0, 0).applyQuaternion(q);
    // privit din spate → oglindă pe X
    spreCamera.copy(camPoz).sub(centru);
    const oglinda = normala.dot(spreCamera) < 0 ? -1 : 1;
    // direcția de citire pe ecran, după oglindire
    p0.copy(centru).project(camera);
    p1.copy(centru).addScaledVector(citire, oglinda).project(camera);
    const dx = p1.x - p0.x;
    const dy = p1.y - p0.y;
    const gresit = dx < -0.02 || (Math.abs(dx) <= 0.02 && dy < 0);
    const rotire = gresit ? -1 : 1;
    o.scale.set(oglinda * rotire, rotire, 1);
  });
}

/** Cutia a ce e FIZIC într-un obiect: materie, fără etichetele și marcajele
 *  HUD (mărimea lor e în pixeli, nu în metri), fără textele cotelor și fără
 *  liderii / liniile de cotă (`segmente`, `triunghiuri`). Pe ea se încadrează scena: clădirea și terenul trebuie să
 *  atingă marginile ecranului, nu eticheta cea mai depărtată. */
export function cutiaFizica(obiect: THREE.Object3D) {
  const cutie = new THREE.Box3();
  obiect.updateWorldMatrix(true, true);
  const alta = new THREE.Box3();
  obiect.traverse((o) => {
    if (o.userData?.hud || o.userData?.cota || o.userData?.adnotare) return;
    const g = (o as THREE.Mesh).geometry;
    if (!g || !o.visible) return;
    if (!g.boundingBox) g.computeBoundingBox();
    if (!g.boundingBox || g.boundingBox.isEmpty()) return;
    alta.copy(g.boundingBox).applyMatrix4(o.matrixWorld);
    cutie.union(alta);
  });
  return cutie;
}

/** Încadrarea unei cutii în ecran: camera rămâne pe direcția ei de privire,
 *  dar se depărtează exact cât trebuie ca cele opt colțuri ale cutiei să
 *  intre în cadru — nu sfera înscrisă, care lasă margini goale late în jurul
 *  unei clădiri turtite. `depasire` cere ca cutia să iasă puțin din ecran
 *  (0,03 = 3 %): un model care atinge marginile se citește mai mare decât unul
 *  care plutește în mijloc. Întoarce distanța aleasă. */
export function incadreazaCutia(camera: THREE.PerspectiveCamera,
                                tinta: THREE.Vector3, cutie: THREE.Box3,
                                depasire = 0.03) {
  const centru = cutie.getCenter(new THREE.Vector3());
  const dir = camera.position.clone().sub(tinta).normalize();   // spre cameră
  if (dir.lengthSq() < 1e-9) dir.set(0.6, -0.7, 0.4).normalize();
  const privire = dir.clone().negate();
  const dreapta = privire.clone().cross(camera.up).normalize();
  if (dreapta.lengthSq() < 1e-9) dreapta.set(1, 0, 0);
  const sus = dreapta.clone().cross(privire).normalize();
  const tgV = Math.tan(THREE.MathUtils.degToRad(camera.fov / 2));
  const tgH = tgV * (camera.aspect || 1);
  const v = new THREE.Vector3();
  const colt = (i: number) => v.set(
    i & 1 ? cutie.max.x : cutie.min.x,
    i & 2 ? cutie.max.y : cutie.min.y,
    i & 4 ? cutie.max.z : cutie.min.z).sub(centru);
  // distanța la care toate colțurile încap, pentru centrul curent
  const distanta = () => {
    let d = 0;
    for (let i = 0; i < 8; i++) {
      colt(i);
      const adancime = v.dot(dir);            // cât e colțul spre cameră
      d = Math.max(d, adancime + Math.abs(v.dot(sus)) / tgV,
                      adancime + Math.abs(v.dot(dreapta)) / tgH);
    }
    return d;
  };
  let d = distanta();
  // În perspectivă colțurile din spate se strâng spre mijloc, deci cutia
  // centrată pe centrul ei geometric atinge o margine și lasă loc gol la
  // cealaltă. Ținta se mută până ce proiecția e simetrică, în câțiva pași.
  for (let pas = 0; pas < 4; pas++) {
    let xmin = Infinity, xmax = -Infinity, ymin = Infinity, ymax = -Infinity;
    for (let i = 0; i < 8; i++) {
      colt(i);
      const den = Math.max(d - v.dot(dir), 1e-6);
      const xp = v.dot(dreapta) / den;
      const yp = v.dot(sus) / den;
      xmin = Math.min(xmin, xp); xmax = Math.max(xmax, xp);
      ymin = Math.min(ymin, yp); ymax = Math.max(ymax, yp);
    }
    centru.addScaledVector(dreapta, (xmax + xmin) / 2 * d)
          .addScaledVector(sus, (ymax + ymin) / 2 * d);
    d = distanta();
  }
  d *= 1 - depasire;
  tinta.copy(centru);
  camera.position.copy(centru).addScaledVector(dir, d);
  camera.near = Math.max(0.05, d / 5000);
  camera.far = d + cutie.getSize(new THREE.Vector3()).length() * 3;
  camera.updateProjectionMatrix();
  return d;
}

/** Materialele HUD-ului: o singură grosime, o singură opacitate. */
export function materialLinie(culoare: string, opacitate = 0.85) {
  return new THREE.LineBasicMaterial({
    color: new THREE.Color(culoare), transparent: true, opacity: opacitate,
    depthTest: false,
  });
}

/** Săgețile cotelor: triunghiuri pline, în aceeași cerneală ca linia. */
export function materialSageata(culoare: string) {
  return new THREE.MeshBasicMaterial({
    color: new THREE.Color(culoare), side: THREE.DoubleSide,
    depthTest: false, transparent: true, opacity: 0.95,
  });
}

/** `THREE.Mesh` de triunghiuri dintr-o listă plată de coordonate. */
export function triunghiuri(coord: number[], material: THREE.Material) {
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.Float32BufferAttribute(coord, 3));
  const m = new THREE.Mesh(geo, material);
  m.renderOrder = 9;
  m.userData.adnotare = true;
  return m;
}

/** Martorii cotelor: linie continuă, subțire, mai stinsă decât linia de cotă.
 *  Punctați și la 0,45 opacitate nu se vedeau deloc pe un martor de un metru,
 *  deci cota părea fără capete. */
export function materialMartor(culoare: string) {
  return new THREE.LineBasicMaterial({
    color: new THREE.Color(culoare), transparent: true, opacity: 0.7,
    depthTest: false,
  });
}

/** `THREE.LineSegments` dintr-o listă plată de coordonate. `punctat` cere
 *  distanțele de linie, altfel materialul dashed nu are ce întrerupe. */
export function segmente(coord: number[], material: THREE.Material,
                         punctat = false) {
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.Float32BufferAttribute(coord, 3));
  const ls = new THREE.LineSegments(geo, material);
  if (punctat) ls.computeLineDistances();
  ls.renderOrder = 9;
  ls.userData.adnotare = true;              // scris, nu materie: nu intră în încadrare
  return ls;
}
